import {
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Inject,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { MediaQueriesService } from '../../../shared/services/utilities/media-queries.service';
import { KzDropdownItemDirective } from './kz-dropdown-item.directive';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { takeUntil, tap } from 'rxjs/operators';
import { KzDropdownService } from './kz-dropdown.service';
import { DropdownPosition, DropdownSize } from './dropdown-options';
import { KzIconsRegistryService } from '../icon/kz-icons-registry.service';
import { kzIconsArrowdown } from '../icon/generated-icons/kzIcons-arrowdown.icon';
import { kzIconsCross } from '../icon/generated-icons/kzIcons-cross.icon';
import { kzIconsArrowleft } from '../icon/generated-icons/kzIcons-arrowleft.icon';

@Component({
  selector: 'kz-dropdown',
  host: {
    '[class]': '"kz-dropdown" + (classes ? " " + classes : "")',
  },
  templateUrl: './kz-dropdown.component.html',
  styleUrls: ['./kz-dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class KzDropdownComponent implements OnInit, OnChanges, OnDestroy {

  opened$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  _isMobile = false;

  @Input() hasSelectedItem = false;
  @Output() reset: EventEmitter<Event> = new EventEmitter<Event>();

  @ContentChildren(KzDropdownItemDirective)
  contentItems: QueryList<KzDropdownItemDirective>;

  private unsubscribe$: Subject<boolean> = new Subject<boolean>();

  private _classes: string;

  get classes(): string {
    return this._classes;
  }

  private _dropdownSize: DropdownSize = 'default';

  get dropdownSize(): DropdownSize {
    return this._dropdownSize;
  }

  @Input()
  set dropdownSize(value: DropdownSize) {
    this._dropdownSize = value;
  }

  private _dropdownPosition: DropdownPosition = 'center-from-parent';

  get dropdownPosition(): DropdownPosition {
    return this._dropdownPosition;
  }

  @Input()
  set dropdownPosition(value: DropdownPosition) {
    this._dropdownPosition = value;
  }

  constructor(
    public mediaQueriesService: MediaQueriesService,
    private _kzDropdownService: KzDropdownService,
    private _changeDetectorRef: ChangeDetectorRef,
    private kzIconsRegistryService: KzIconsRegistryService,
    @Inject(DOCUMENT) protected _document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.kzIconsRegistryService.registerIcons([kzIconsArrowdown, kzIconsArrowleft, kzIconsCross]);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this._updateClasses();
    this._changeDetectorRef.detectChanges();
    if (isPlatformBrowser(this.platformId)) {
      combineLatest([
        this.opened$,
        this.mediaQueriesService.isMobile().pipe(
          tap((isMobile) => this._isMobile = isMobile),
        )
      ])
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(([opened, isMobile]) => this._document.body.style.overflow = (opened && isMobile) ? 'hidden' : 'auto'),
      ).subscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._updateClasses();
    this._changeDetectorRef.detectChanges();
  }

  private _updateClasses() {
    this._classes = '';

    if (this.dropdownSize && this.dropdownSize !== 'default') {
      this._classes += ` kz-dropdown-size--${ this.dropdownSize }`;
    }
    if (this.dropdownPosition) {
      this._classes += ` kz-dropdown-pos--${ this.dropdownPosition }`;
    }

  }

  resetSearch(e): void {
    e.stopPropagation();
    this.reset.emit(e);
  }

  toggleSearch(e, forceClose = false, clickOutside = false): void {
    e.stopPropagation();

    if (this.opened$.getValue() === true || forceClose) {
      if (this._isMobile && clickOutside) {
        this._changeDetectorRef.detectChanges();
        return;
      }
      this.opened$.next(false);
    } else {
      this._kzDropdownService.dropdowns.map((dropdown) => {
        dropdown.opened$.next(false);
      })
      this.opened$.next(true);
      this._kzDropdownService.dropdowns.push(this);
    }
    this._changeDetectorRef.detectChanges();
  }
}
